<template>
    <viewer-tab ref="tab" title="Carrier Modeler" :typename="$options.typename"
                v-loading="loading" element-loading-text="Saving"
                :url-help="$urls.HELP_MODEL_CARRIER" :click-x="clickX" :accept="handleAccept"
                @opened="handleTabOpened" @active="handleTabActive">
        <el-tooltip slot="header-buttons" placement="bottom" :open-delay="$defaults.tooltipDelay"
                    content="Carriers are rotational reference frames with eccentrically mounted parts.">
            <span style="margin-right: 10px"><i class="el-icon-info menu-icon-btn"/></span>
        </el-tooltip>
        <div class="pane-grid-1-2">
            <div class="pane-1">
                <!--INFO-->
                <ModelerInfo ref="formInfo" :form-info="formInfo"/>

                <!--GEOMETRY-->
                <div class="section-heading">
                    <h4>Geometry</h4>
                    <hr>
                </div>
                <el-form ref="formGeom"
                         class="frame-form"
                         :model="formGeom"
                         :rules="$options.rulesGeom"
                         size="mini"
                         hide-required-asterisk
                         :show-message="false">
                    <el-form-item class="form-item" prop="pitchCircleDiameter" label="Pitch circle diameter">
                        <el-input v-model="formGeom.pitchCircleDiameter" class="form-field" @change="handleChangeGeom">
                            <template slot="suffix">mm</template>
                        </el-input>
                    </el-form-item>
                    <!--BUG: https://forum.framework7.io/t/vue-pressing-enter-key-in-input-causes-app-to-reload/2585/10-->
                    <el-form-item hidden>
                        <el-input/>
                    </el-form-item>
                </el-form>

                <!--ASSEMBLE-->
                <div class="section-heading">
                    <h4>Assemble</h4>
                    <hr>
                    <div style="margin-left: 1em">
                        <el-checkbox v-model="formGeom.usePitchCircle" @change="handleChangeEqualRadii">Pitch circle</el-checkbox>
                        <el-checkbox v-model="formGeom.useEqualSpacing" @change="handleChangeEqualSpacing">Equal spacing</el-checkbox>
                    </div>
                </div>
                <el-form ref="formPlanets"
                         class="frame-form"
                         :model="formPlanets"
                         size="mini"
                         hide-required-asterisk
                         :show-message="false">
                    <div class="frame-form-items-h ffi-1s-3i">
                        <span/>
                        <span/>
                        <span>$r$ <small class="units">(mm)</small></span>
                        <span>$\phi$ <small class="units">({{ $options.units.angle }})</small></span>
                        <span>$z$ <small class="units">(mm)</small></span>
                    </div>
                    <div v-for="(planet, i) in formPlanets.planets" :key="planet.key">
                        <div class="frame-form-items ffi-1s-3i">
                            <el-form-item class="form-item">
                                <el-button class="frame-remove-btn" type="text" @click="handleRemovePlanet(i)">
                                    <i class="el-icon-remove-outline"/>
                                </el-button>
                            </el-form-item>
                            <el-form-item class="form-item" :prop="`planets.${i}.Id`"
                                          :rules="$options.rulesConnect.planet" label-width="0">
                                <el-select
                                        class="w-100"
                                        placeholder="Select gear"
                                        v-model="formPlanets.planets[i].Id"
                                        clearable
                                        filterable
                                        no-match-text="No matching gears"
                                        no-data-text="No mountable gears"
                                        default-first-option
                                        @change="handleChangePlanet">
                                    <el-option
                                            v-for="item in filterParts(planet)"
                                            :key="item.Id"
                                            :label="item.label"
                                            :value="item.Id">
                                        <span class="select-option-label1">{{ item.label }}</span>
                                        <span class="select-option-label2">{{ item.coordStr }}</span>
                                    </el-option>
                                </el-select>
                            </el-form-item>
                            <el-form-item class="form-item pos-form-item" :prop="`planets.${i}.r`" :rules="$options.rulesConnect.r">
                                <el-input v-model="formPlanets.planets[i].r" placeholder="r" @change="handleChangePlanet" :disabled="formGeom.usePitchCircle"/>
                            </el-form-item>
                            <el-form-item class="form-item pos-form-item" :prop="`planets.${i}.phi`" :rules="$options.rulesConnect.phi">
                                <el-input v-model="formPlanets.planets[i].phi" placeholder="phi" @change="handleChangePlanet" :disabled="formGeom.useEqualSpacing"/>
                            </el-form-item>
                            <el-form-item class="form-item pos-form-item" :prop="`planets.${i}.z`" :rules="$options.rulesConnect.z">
                                <el-input v-model="formPlanets.planets[i].z" placeholder="z"/>
                            </el-form-item>
                        </div>
                    </div>
                    <el-button class="frame-add-btn" size="small" icon="el-icon-plus" @click="handleAddPlanet">
                        Add Planet
                    </el-button>
                </el-form>
            </div>
            <div class="pane-2 pane-2-pixi">
                <canvas ref="pixi" class="pixi"/>
                <div class="pane-2-message" v-show="!hasDefinedAssembly">
                    <small class="text-info">carrier assembly preview</small>
                </div>
            </div>
            <div class="pane-3 pane-3-tables">
                <modeler-tables :tables="tables"/>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import * as PIXI from "../../config/pixi";
  import ViewerTab from "../viewer/ViewerTab";
  import ModelerTables from "../_tables/ModelerTables";
  import ModelerInfo from "../_forms/ModelerInfo";
  import {copyDeep, ResizeListener, subtractArrays} from "../../assets/js/utils";
  import {rules} from "../../assets/js/validators";
  import math from "../../assets/js/math";
  import coord from "../../assets/js/coord";
  import pixi from "../../assets/js/pixi/parts";
  import requests from "../../requests";
  import {EventBus} from "../../event_bus";
  import gql from "graphql-tag";

  export default {
    name: "ModelCarrier",
    components: {ModelerTables, ModelerInfo, ViewerTab},
    props: {
      clickX: Function,
      openId: String,
      name: String,  // unique tab name
    },

    // STATIC DATA
    typename: requests.gear.TYPENAME.FRAME,
    labelWidth: {
      col1: '135px',
      col2: '135px',
    },
    units: {
      angle: 'deg',
    },
    rulesGeom: {
      pitchCircleDiameter: rules.requiredPositiveNumber,
    },
    rulesConnect: {
      planet: rules.required,
      r: rules.requiredPositiveNumber,
      phi: rules.requiredNumber,
      z: rules.requiredNumber,
    },

    data() {
      return {
        // APOLLO
        parts: [],
        editPart: null,
        loading: false,

        // INIT
        editPlanets: [],

        // FORMS
        formInfo: {
          name: '',
          number: '',
          description: '',
        },
        formGeom: {
          pitchCircleDiameter: '',
          useEqualSpacing: true,
          usePitchCircle: true,
        },
        formPlanets: {
          planets: [],
        },
        validGeom: false,

        // TABLE
        docSharing: {},  // owner, shared, lock
        docSigned: {},  // lastModifiedOn, createdOn, etc.

        // PIXI
        pixi: null,
        pixiCarrier: null,
        pixiPlanets: null,
        resizeListener: null,
      }
    },
    computed: {
      isEditing() {
        // checks if editing an existing object
        return typeof this.openId !== 'undefined';
      },
      selectedParts() {
        return this.formPlanets.planets;
      },
      hasDefinedAssembly() {
        // Returns boolean to indicate if carrier and any planets are defined.
        const definedPlanets = this.selectedParts.filter(p => p.Id !== '').length > 0;
        return this.validGeom || definedPlanets;
      },
      tableFile() {
        return this.$handlers.lib.tableFile({
          ...this.formInfo,
          ...this.docSharing,
          ...this.docSigned,
          owner: this.$store.state.session.user.Id,
        });
      },
      tables() {
        return [{
          table: this.$handlers.model.tableAssm(this.editPart),
          title: 'Assembly'
        }, {
          table: this.tableFile,
          title: 'File',
        }]
      },
    },
    watch: {
      // https://stackoverflow.com/questions/42133894/vue-js-how-to-properly-watch-for-nested-data/42134176
      selectedParts() {
        if (this.formGeom.useEqualSpacing) this.computeEqualSpacing();
        if (this.formGeom.usePitchCircle) this.computeEqualRadii();
        this.updatePixi();
      },
    },
    apollo: {
      parts: {
        fetchPolicy: 'cache-only',
        variables() {return {Id: this.$store.state.model.doc.Id}},
        query() {return this.$store.state.model.getPartsQuery},
        update({ gearsModel: { parts } }) {
          // filter for mountable shafts and gears
          const mountableParts = parts.filter(p => {
            const isShaft = this.$handlers.model.isPartFrameShaft(p);
            const isGear = this.$handlers.model.isPartGear(p);
            const isExternal = !this.$handlers.gear.isInternal(p.obj.geom);
            const isExternalGear = isGear && isExternal;

            if (isExternalGear || isShaft) {
              const isNotMounted = !p.frame;
              const isMountedToSelf = isNotMounted ? false : p.frame.Id === this.openId;
              return isNotMounted || isMountedToSelf;  // is mountable
            } else {
              return false;
            }
          });

          // map data required by form and graphics
          return mountableParts.map(part => {
            return {
              Id: part.Id,
              label: this.$handlers.model.partLabel(part),
              coordStr: coord.formatCoordStr(this.$handlers.model.posGlobal(part)),
            }
          });
        },
      },
      editPart: {
        fetchPolicy: 'cache-and-network',
        skip() { return !this.isEditing || this.editPart },  // queries *once*
        variables() { return { Id: this.openId } },
        query: gql`query getPartFrame ($Id: ID!) {
            editPart: part (Id: $Id) {
              ${requests.model.partFrameQueryStr}
            }
          }`,
        result({ data: {editPart} }) {
          // NOTE: `pos` and `parent` fields required to calculate relative position of planets.

          // get existing planets and initialize form data
          this.editPlanets = this.$store.getters.getSubframes(editPart);
          this.mapPlanets(this.editPlanets);

          // map lib object to form data; deep copy to avoid mutating Apollo cache
          let libObj = copyDeep(editPart.obj);
          [this.formInfo, this.docSharing, this.docSigned, this.formGeom] = this.$handlers.lib.libObjectForms(libObj);
          this.validGeom = true;

          // update graphics
          if (!this.pixi) this.newPixi();
          this.updatePixi();
        },
      },
    },

    methods: {
      // HELPERS
      planetsRemoved() {
        // Assess which planets have been removed based on form selections.
        // Returns array of part Ids.

        const existingPlanets = this.editPlanets.map(p => p.Id);
        const selectedPlanets = this.formPlanets.planets.map(p => p.Id);

        return existingPlanets.filter(existingId => !selectedPlanets.includes(existingId));
      },

      // APOLLO
      async mutateCreate() {
        // API mutation to create new lib frame and add as model part.
        // Returns added part.

        this.loading = true;

        const payloadLib = {
          ...this.formInfo,
          lock: false,
          shared: [],
          geom: {...this.formGeom},
        };

        return await this.$store.dispatch('addPartFrame', payloadLib)
          .finally(() => this.loading = false);
      },
      async mutateUpdate(Id) {
        // API mutation to update existing lib frame.
        // Returns nothing.

        this.loading = true;
        const payload = {
          Id: Id,
          ...this.formInfo,
          geom: {...this.formGeom}
        };

        await this.$store.dispatch('updateFrame', payload)
          .finally(() => this.loading = false);

        // trigger global event to redraw the affected part
        const part = this.$store.getters.getPart(this.editPart.Id);
        EventBus.$emit('redraw-part', part);
      },
      async mutateUpdatePlanets(partFrame) {
        // Update `frame` field of planet parts.
        // Returns nothing.

        this.loading = true;

        // construct payload for updating all planets specified in form
        const planetsConnected = this.formPlanets.planets.map(planet => {
          const phi = math.angle2rad(planet.phi, this.$options.units.angle);
          const pos = coord.cylindricalToCartesian(planet.r, phi, planet.z);
          return {
            Id: planet.Id,
            frame: partFrame.Id,
            parent: partFrame.Id,
            pos: pos,
            rot: partFrame.rot,
          }
        });

        // construct payload for updating any planets removed from carrier
        const planetsRemoved = this.planetsRemoved().map(planetId => {
          return {
            Id: planetId,
            frame: null,
            parent: null,
            pos: this.$requests.model.ORIGIN,
            rot: this.$requests.model.EYE,
          }
        });

        await this.$store.dispatch('updateParts', [...planetsConnected, ...planetsRemoved])
          .finally(() => this.loading = false);
      },

      // EVENTS
      handleTabOpened() {
        // Called after tab has opened.

        // render math typeset
        window.MathJax.typeset();

        // initialize graphics
        if (!this.pixi) this.newPixi();
        this.onViewerResize();
      },
      handleTabActive() {
        // Call viewer resize callback to prevent loss of pixi view if viewer size is changed while not on this tab.
        this.onViewerResize();
      },
      onViewerResize() {
        // resize the renderer
        const parent = this.pixi.view.parentNode;
        this.pixi.renderer.resize(parent.clientWidth, parent.clientHeight);

        // resize graphics
        this.fitPixi();
      },

      // FORM - CARRIER
      handleChangeGeom() {
        // Event handler for change of carrier geometry.

        this.$refs.formGeom.validate(valid => {
          this.validGeom = valid;
          if (valid) {
            if (this.formGeom.usePitchCircle) this.computeEqualRadii();
            this.updatePixi();
          } else {
            this.resetPixi();
          }
        });
      },

      // FORM - PLANETS
      mapPlanets(planets) {
        // Map planets to form data.
        // Notes about positioning:
        //  1. Form data assumes planets are positioned relative to carrier.
        //  1. However, no requirement exists for planets to be positioned relative to the carrier.
        //  2. Therefore global positions are used to calculate relative positioning when mapping data to form.

        // initialize
        this.formPlanets.planets = [];
        let planetPosGlobal, relativePos, r, phi, z;
        const carrierPosGlobal = this.$handlers.model.posGlobal(this.editPart);

        planets.forEach((planet, i) => {
          // calculate cylindrical coord of each planet relative to the carrier
          planetPosGlobal = this.$handlers.model.posGlobal(planet);
          relativePos = subtractArrays(planetPosGlobal, carrierPosGlobal);
          [r, phi, z] = coord.cartesianToCylindrical(...relativePos);

          this.formPlanets.planets.push({
            key: i,
            Id: planet.Id,
            r: r,
            phi: this.$options.units.angle === 'deg' ? math.rad2deg(phi) : phi,
            z: z,
          })
        });
      },
      filterParts(planet) {
        // Filter parts to exclude the *other* planets already selected or mounted; does not filter out planet arg.
        // `planet` arg should be the currently selected planet for the particular form field being filtered.

        let isCurrentPart, isNotSelected;

        return this.parts.filter(part => {
            isCurrentPart = part.Id === planet.Id;

            if (!isCurrentPart) {
              isNotSelected = !this.formPlanets.planets.find(pl => part.Id === pl.Id);
            }

            return isCurrentPart || isNotSelected;
          }
        )
      },
      handleAddPlanet() {
        // Add form entry for one additional planet.

        // default z to match last planet, otherwise zero
        const N = this.formPlanets.planets.length;
        const z = N !== 0 ? this.formPlanets.planets[N-1].z : '0';

        this.formPlanets.planets.push({
          key: N + 1,
          Id: '',
          r: '',
          phi: '',
          z: z,
        })
      },
      handleChangePlanet() {
        // Event handler for change of a selected planet or a planet property.
        this.updatePixi();
      },
      handleRemovePlanet(index) {
        // Remove form entry for specified planet index.

        this.formPlanets.planets.splice(index, 1);

        // reindex the keys
        this.formPlanets.planets.forEach((planet, i) => {
          planet.key = i;
        });
      },
      handleChangeEqualSpacing(equalSpacing) {
        // Event handler for change to equal spacing checkbox.
        if (equalSpacing) {
          this.computeEqualSpacing();
          this.updatePixi();
        }
      },
      handleChangeEqualRadii(equalRadii) {
        // Event handler for change to equal radii checkbox.
        if (equalRadii) {
          this.computeEqualRadii();
          this.updatePixi();
        }
      },
      computeEqualSpacing() {
        // If marked as equally spaced, calculate polar angle of each planet specified in the form.
        // Only call this if equally spaced condition is true.

        const N = this.formPlanets.planets.length;
        const delta = this.$options.units.angle === 'deg' ? 360 / N : 2 * Math.PI / N;

        // this.formPosition.phis = [];
        for (let i=0; i < N; i++) {
          this.formPlanets.planets[i].phi = i * delta;
        }
      },
      computeEqualRadii() {
        // Calculate radial position of planet specified by the carrier pitch circle diameter.
        // Only call this if equal radii condition is true.

        const pcr = this.formGeom.pitchCircleDiameter / 2;
        this.formPlanets.planets.forEach(planet => {
          planet.r = pcr;
        })
      },

      // FORM - VALIDATE
      async validateCarrier() {
        // Validates form data, otherwise throws error.
        // Beware the number zero can cause issues that do not catch; but string zero is okay.
        // Returns nothing.

        try {
          // validate, throwing error if invalid
          await this.$refs.formInfo.validate();
          await this.$refs.formGeom.validate();
        } catch (e) {
          const msg = 'Invalid carrier data';
          this.$message({
            type: 'error',
            center: true,
            message: msg,
          });
          // propagate error
          throw msg;
        }
      },
      validatePlanets() {
        // Validate planet form data, otherwise throw error.
        // BUG WORKAROUND validate() not working, so using validateField()
        // Returns nothing.

        // get each planet prop to validate; excludes 'key'
        let planetProps = [];
        if (this.formPlanets.planets.length !== 0) {
          planetProps = Object.keys(this.formPlanets.planets[0]).filter(e => e !== 'key');
        }

        // validate position data
        Object.keys(this.formPlanets.planets).forEach((planet, index) => {
          planetProps.forEach(prop => {
            this.$refs.formPlanets.validateField(`planets.${index}.${prop}`, (eMsg) => {
              if (eMsg !== '') {
                const msg = 'Invalid planet data';
                this.$message({
                  type: 'error',
                  center: true,
                  message: msg,
                });
                // propagate error
                throw msg;
              }
            });
          })
        });
      },

      // FORM - COMPLETE
      async handleAccept() {
        return this.isEditing ? await this.handleUpdate() : await this.handleCreate();
      },
      async handleCreate() {
        // Create a new frame if all forms are valid.

        const hasPlanets = this.formPlanets.planets.length !== 0;

        // validate; otherwise throw error
        await this.validateCarrier();
        if (hasPlanets) this.validatePlanets();

        // API mutation to create frame
        const partFrame = await this.mutateCreate();

        // API mutation to update planet parts, if specified
        if (hasPlanets) await this.mutateUpdatePlanets(partFrame);

        // close tab
        this.handleClose();
      },
      async handleUpdate() {
        // Update the frame if all forms are valid.

        // validate; otherwise throw error
        await this.validateCarrier();
        this.validatePlanets();

        // API mutations to update lib frame and planet parts
        await this.mutateUpdate(this.editPart.obj.Id);
        await this.mutateUpdatePlanets(this.editPart);

        // close tab and notify user
        this.handleClose();
        // this.$notify.success({
        //   position: this.$defaults.notifyPosition,
        //   title: 'Updated Carrier',
        //   message: this.formInfo.name + ' saved',
        // });
      },
      handleClose() {
        // Call close method on closable tab to play transition.
        // https://stackoverflow.com/questions/40957008/how-to-access-to-a-child-method-from-the-parent-in-vue-js/40957171
        this.$refs.tab.handleClose();
      },

      // GRAPHICS
      newPixi() {
        this.pixi = new PIXI.Application({ view: this.$refs.pixi, transparent: true, antialias: true });

        // initialize carrier and planets; planets layer above gear
        this.pixiCarrier = new PIXI.Graphics();
        this.pixiPlanets = new PIXI.Graphics();
        this.pixi.stage.addChild(this.pixiCarrier);
        this.pixi.stage.addChild(this.pixiPlanets);

        // starter event listeners for resizing renderer
        const viewer = document.getElementById('viewer');
        this.resizeListener = new ResizeListener(viewer, this.onViewerResize);
      },
      fitPixi() {
        // Center and scale the pixi carrier assembly to provide a consistent view regardless of assembly size.
        // Call BEFORE carrier and planets are drawn since the stage scale property is required for scaling linewidth.

        const view = this.pixi.view;

        // horizontally and vertically center the carrier assembly drawn at (0, 0)
        const xStage = view.clientWidth / 2;
        const yStage = view.clientHeight / 2;

        // get spatial limits of assembly
        let part, diameter;
        const envelopeDiameters = this.selectedParts.map(p => {
          part = this.$store.getters.getPart(p.Id);

          if (part) {
            ({diameter} = this.$handlers.model.partSize(part));
            return 2 * parseFloat(p.r) + diameter;
          } else {
            return 0;
          }
        });

        // scale pixi stage to show entire assembly
        const scalingDimension = 1.1 * Math.max(...envelopeDiameters, 1.25 * this.formGeom.pitchCircleDiameter);
        let scale = Math.min(view.clientWidth, view.clientHeight) / scalingDimension;

        // position (pan) and scale (zoom) the pixi stage
        this.pixi.stage.position.set(xStage, yStage);
        this.pixi.stage.scale.set(scale, -scale);  // y-axis upward
      },
      resetPixi() {
        this.pixiCarrier.clear();
        this.pixiPlanets.clear();
        this.pixiPlanets.removeChildren();  // text
      },
      updatePixi() {
        // get part data
        const partsData = this.selectedParts.map(p => {
          return {
            part: this.$store.getters.getPart(p.Id),
            r: parseFloat(p.r),
            phi: parseFloat(p.phi),
            z: parseFloat(p.z),
          }
        });

        // set positions and scale
        this.fitPixi();

        // draw carrier and planet graphics
        pixi.carrierFrame(this.pixiCarrier, this.formGeom.pitchCircleDiameter);
        pixi.carrierPlanets(this.pixiPlanets, partsData, this.$options.units.angle);

        // PLACEHOLDER {carrier} tooltip to display planet label on hover
        // https://github.com/pixijs/pixi.js/issues/4983
      },
    },
    beforeDestroy() {
      // stop ticker and destroy pixi
      this.pixi.stop();
      this.pixi.destroy(false, {children: true});

      // remove resize listener and observer
      this.resizeListener.destroy();
    }
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/layout-tab-pane-1-2.scss"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped src="./assets/modeler-frame.css"/>
